import { useEffect, useState } from 'react';
import { detectVisitorCity } from '../services/api';

const LS_KEY = 'devb_detected_city_v1';
const TTL_MS = 24 * 60 * 60 * 1000; // 24h client-side cache

/**
 * Detects the visitor's Turkish city (from IP, backend proxied).
 * Caches for 24h in localStorage. Fails soft — returns {detected:false, ...} on any error.
 *
 * @returns {{
 *   city: string|null, cityId: string|null, slug: string|null,
 *   countryCode: string|null, detected: boolean, loading: boolean
 * }}
 */
export default function useDetectedCity() {
  const [state, setState] = useState(() => {
    if (typeof window === 'undefined') return { loading: true, detected: false };
    try {
      const raw = window.localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.expiresAt && parsed.expiresAt > Date.now()) {
          return { ...parsed.data, loading: false };
        }
      }
    } catch (_) { /* ignore */ }
    return { loading: true, detected: false, city: null, cityId: null, slug: null, countryCode: null };
  });

  useEffect(() => {
    // Skip if already cached (loading=false from init)
    if (!state.loading) return;
    let alive = true;
    (async () => {
      try {
        const res = await detectVisitorCity();
        const data = res?.data || {};
        if (!alive) return;
        setState({ ...data, loading: false });
        try {
          window.localStorage.setItem(LS_KEY, JSON.stringify({
            data, expiresAt: Date.now() + TTL_MS
          }));
        } catch (_) { /* quota / private mode: ignore */ }
      } catch (_) {
        if (alive) setState({ loading: false, detected: false, city: null, cityId: null, slug: null, countryCode: null });
      }
    })();
    return () => { alive = false; };
  }, [state.loading]);

  return state;
}
